import isEmpty from 'lodash.isempty';
import { retrieveFromSessionStorage } from './sessionStorageHelper';
import { WIZARD, WIZARD_DATA } from '../../constants/sessionStorage';

export const getTierForAmount = (tiers, cashAmount) => {
  if (!tiers || isEmpty(tiers)) {
    return null;
  }
  const searchTiers = JSON.parse(JSON.stringify(tiers));
  if (searchTiers[0].LowBound !== 0) {
    searchTiers[0].LowBound = 0;
  }

  return searchTiers.find((tier) => {
    const max = tier.HighBound === null ? Number.MAX_VALUE : tier.HighBound;
    const min = tier.LowBound === null ? 0 : tier.LowBound;
    return max >= cashAmount && min <= cashAmount;
  });
};

export const getFineWeight = (isProductPage) => {
  let fineWeight = 0;
  if (isProductPage) {
    fineWeight = retrieveFromSessionStorage(WIZARD_DATA)?.productPrices
      ?.FineWeightDefault;
  }
  if (!fineWeight) {
    fineWeight = retrieveFromSessionStorage(WIZARD)?.Product?.FineWeightDefault;
  }
  return fineWeight;
};

export const lockedPriceCashEstimatedSubtotal = (
  cashAmount,
  tiers,
  setPricePerUnit,
  isProductPage,
  unitPrice,
) => {
  if (!tiers) {
    return {
      estimatedOrderTotal: cashAmount,
      volumeDiscount: 0,
      newPricePerUnit: unitPrice,
    };
  }
  /* ***********************************************************
     ****** Cash-based quantity calculation **********************
     *************************************************************
     On Sep 20th 2023, it was decided that Quantity review 
     would be defaulted for "locked-price customers opting
     for Cash purchase", following which this new calculation
     was defined by @ZachLincoln of GBI.
     ********************************************************** */
  const fineWeight = getFineWeight(isProductPage);

  // Step 1: Use the cash amount entered to select a tier
  const selectedTier = getTierForAmount(tiers, cashAmount);
  if (!selectedTier) {
    return {
      estimatedOrderTotal: cashAmount,
      volumeDiscount: 0,
      newPricePerUnit: unitPrice,
    };
  }
  // Step 2: Using the selected tier, determine the quantity that can be purchased
  const { PricePerOz } = selectedTier;
  const tierBasedQuantity = Math.trunc(
    Number(cashAmount / (PricePerOz * fineWeight).toFixed(4)),
  );

  // Step 3. Get Price pr oz from worst tier
  const worstTierPPO = tiers[0].PricePerOz;

  // Step 3a. Find PricePerOz in the worst tier to get Subtotal 0
  const worstTierSubTotal = Number(
    (tierBasedQuantity * worstTierPPO * fineWeight).toFixed(2),
  );
  // Step 4: Use subtotal to find appropriate tier range and price
  const recalculatedTier = tiers.find((tier) => {
    const max = tier.HighBound === null ? Number.MAX_VALUE : tier.HighBound;
    const min = tier.LowBound === null ? 0 : tier.LowBound;
    return max >= worstTierSubTotal && min <= worstTierSubTotal;
  });

  if (!recalculatedTier || isEmpty(recalculatedTier)) {
    setPricePerUnit(PricePerOz * fineWeight);
    return {
      estimatedOrderTotal: cashAmount,
      volumeDiscount: 0,
      newPricePerUnit: PricePerOz * fineWeight,
    };
  }

  // Step 5:  Total = Quantity * appropriate tier PricePerOz * FineWeight
  const recalculatedTierSubTotal = Number(
    (recalculatedTier.PricePerOz * tierBasedQuantity * fineWeight).toFixed(2),
  );

  /* ****** END Cash-based quantity calculation ************* */

  setPricePerUnit(recalculatedTier.PricePerUnit);
  return {
    estimatedOrderTotal: recalculatedTierSubTotal,
    volumeDiscount: worstTierSubTotal - recalculatedTierSubTotal,
    newPricePerUnit: recalculatedTierSubTotal / tierBasedQuantity,
  };
};

export const formatTierValue = (value) => {
  if (value >= 1_000_000) return `$${Math.round(value / 1_000_000)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${value}`;
};

export const getActiveTierLabel = (tiers, totalPrice) => {
  if (!tiers?.length) return null;
  const activeTierIndex = tiers.findIndex(
    (tier) =>
      totalPrice >= tier.LowBound &&
      (tier.HighBound === null || totalPrice < tier.HighBound),
  );
  if (activeTierIndex <= 0) return null;
  return formatTierValue(tiers[activeTierIndex].LowBound).replace('$', '');
};

export const lockedPriceQuantityEstimatedSubtotal = (
  quantityAmount,
  tiers,
  setPricePerUnit,
  isProductPage,
  unitPrice,
) => {
  if (!tiers) {
    return {
      estimatedOrderTotal: quantityAmount,
      volumeDiscount: 0,
      newPricePerUnit: unitPrice,
    };
  }

  const fineWeight = getFineWeight(isProductPage);

  // Step 1: Order Amount = Price Per Oz * Quantity * FineWeightDefault
  const worstTierOrderSubtotal = Number(
    (tiers[0].PricePerOz * quantityAmount * fineWeight).toFixed(2),
  );

  // Step 2: Get tier for worst tier order amount
  const tierForWorstTierOrderAmount = getTierForAmount(
    tiers,
    worstTierOrderSubtotal,
  );

  // Step 3: Calculate new subtotal using new tier
  const recalculatedSubtotal = Number(
    (
      tierForWorstTierOrderAmount.PricePerOz *
      quantityAmount *
      fineWeight
    ).toFixed(2),
  );

  // Step 3a. Get tier for newSubtotal
  const finalTier = getTierForAmount(tiers, recalculatedSubtotal);
  if (!finalTier || isEmpty(finalTier)) {
    return {
      estimatedOrderTotal: recalculatedSubtotal,
      volumeDiscount: 0,
      newPricePerUnit: unitPrice,
    };
  }

  // Step 4: Calculate difference in subtotals for Volume Discount
  setPricePerUnit(finalTier.PricePerUnit);
  return {
    estimatedOrderTotal: recalculatedSubtotal,
    volumeDiscount: worstTierOrderSubtotal - recalculatedSubtotal,
    newPricePerUnit: recalculatedSubtotal / quantityAmount,
  };
};
